import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { OrganizationService } from '../../../shared/services/organization.service';
import { contractOutput } from '#backend/contract';
import { CommonModule } from '@angular/common';
import { UserService } from '../../services/user.service';
import { EnhancedLoadingService } from '../../services/enhanced-loading.service';
import type { Culture } from '#backend/entity/loaded/harvest-monitoring/Culture';

type Organization = contractOutput['organization']['list']['items'][number];

export interface MonitoringFilterModalParams {
  organizationId?: number | null;
  organizationName?: string;
  safra?: string;
  cultura?: {
    id: number;
    description: string;
    enable: boolean;
  };
  status?: string;
}

@Component({
  selector: 'app-monitoring-filter-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
  ],
  templateUrl: './monitoring-filter-modal.component.html',
  styleUrl: './monitoring-filter-modal.component.scss',
})
export class MonitoringFilterModalComponent implements OnInit {
  private readonly smartClient = inject(SmartOrpcClientService);
  private readonly organizationService = inject(OrganizationService);
  private readonly userService = inject(UserService);
  readonly loadingService = inject(EnhancedLoadingService);
  @Output() filterChange = new EventEmitter<MonitoringFilterModalParams>();

  protected cropPeriods: string[] = [];
  protected cropCultures: {
    id: number;
    description: string;
    enable: boolean;
  }[] = [];
  protected organizations: Organization[] = [];
  protected filteredOrganizations: Organization[] = [];

  protected statusOptions = [
    { value: 'VALIDATING_INPUT', label: 'Validando dados de entrada' },
    { value: 'ANALYZING', label: 'Em monitoramento' },
    { value: 'COMPLETED', label: 'Monitoramento concluído' },
    { value: 'FAILED', label: 'Solicitação cancelada pela Geoambiente' },
  ];

  form = new FormGroup({
    organization: new FormControl<Organization | string | null>(null),
    safra: new FormControl(''),
    cultura: new FormControl<Culture | null>(null),
    status: new FormControl(''),
  });

  formControls = this.form.controls;

  async ngOnInit(): Promise<void> {
    this.form.disable();
    const promises = [
      this.loadCropYears(),
      this.getCropCulturesData(),
      this.loadOrganizations(),
    ];
    await Promise.all(promises).finally(() => {
      this.form.enable();
    });

    this.setupOrganizationFilter();
  }

  private async loadOrganizations(): Promise<void> {
    if (!this.userService.isGeoambiente) return;
    this.organizations = await this.organizationService.getOrganizations(
      false,
      true,
    );
    this.filteredOrganizations = this.organizations;
  }

  private setupOrganizationFilter(): void {
    this.formControls['organization'].valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        this.filterOrganizations(value);
      }
    });
  }

  private filterOrganizations(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredOrganizations = this.organizations;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredOrganizations = this.organizations.filter((org) =>
      org.name.toLowerCase().includes(term),
    );
  }

  protected displayOrganizationName(
    organization: Organization | string | null,
  ): string {
    if (!organization) return '';
    if (typeof organization === 'string') {
      // Trata valores especiais
      if (organization === '__ALL__') return 'Todos';
      if (organization === 'Geoambiente') return 'Geoambiente';
      return organization;
    }
    return organization?.name || '';
  }

  private async getCropCulturesData(): Promise<void> {
    const cropCultures = await this.smartClient.client.monitoring.cultures.list(
      {},
    );
    this.cropCultures = cropCultures;
  }

  // Helper method to get a form control
  getFormControl(controlName: keyof typeof this.formControls): FormControl {
    return this.formControls[controlName] || new FormControl('');
  }

  clearFilters(): void {
    Object.values(this.formControls).forEach((control) => {
      control.reset();
    });
    this.filterOrganizations('');
    this.emitFilters();
  }

  applyFilters(): void {
    this.emitFilters();
  }

  cancel(): void {
    // Close menu without applying filters
  }

  private emitFilters(): void {
    const selectedOrg = this.formControls['organization'].value as
      Organization | string | null;

    let organizationIdValue: number | null | undefined;

    if (selectedOrg && typeof selectedOrg === 'object') {
      // É um objeto Organization - envia o ID
      organizationIdValue = selectedOrg.id;
    } else if (typeof selectedOrg === 'string') {
      // Trata valores especiais
      if (selectedOrg === '__ALL__') {
        // Não adiciona filtro de organização para buscar todos
        organizationIdValue = undefined;
      } else if (selectedOrg === 'Geoambiente') {
        // Define como null para buscar usuários sem organização
        organizationIdValue = null;
      }
    } else {
      // Não selecionado
      organizationIdValue = undefined;
    }

    const filterParams: MonitoringFilterModalParams = {
      organizationId: organizationIdValue,
      organizationName:
        typeof selectedOrg === 'object' ? selectedOrg?.name : undefined,
      safra: this.formControls['safra'].value || undefined,
      cultura: this.formControls['cultura'].value || undefined,
      status: this.formControls['status'].value || undefined,
    };

    console.log('Modal emitindo filtros:', filterParams);
    this.filterChange.emit(filterParams);
  }
  /**
   * Carrega as safras disponíveis a partir do banco de dados.
   * - Clientes: apenas as safras das suas próprias solicitações.
   * - Geoambiente: todas as safras de todos os clientes.
   */
  private async loadCropYears(): Promise<void> {
    const result =
      await this.smartClient.safeClient.monitoring.availableCropYears();
    this.cropPeriods = result.data ?? [];
  }

  clearOrganizationField(): void {
    this.form.controls.organization.reset();
    this.filterOrganizations('');
  }
}
